import { Fragment } from "react";
import { useNavigate } from "react-router-dom";
import type {
  DocumentMetadata,
  PreconfirmationField,
  PreconfirmationStep,
} from "../../../types";
import { Option } from "../../../types";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { onUpdatePreconfirmation } from "../../../reducers/preconfirmationReducer";
import {
  onUpdateStep,
  onUpdateSubStep,
} from "../../../reducers/onboardingDataReducer";
import {
  onUpdateStep as onUpdateStepSecurities,
  onUpdateSubStep as onUpdateSubStepSecurities,
} from "../../../reducers/securitiesReducer";
import { Accordion } from "@allaria-tech/matter/Accordion";
import { Button } from "@allaria-tech/matter/Button";
import { Icon } from "@allaria-tech/matter/Icon";
import { Typography } from "@allaria-tech/matter/Typography";
import useWindowWidth from "../../../hooks/useWindowWidth";

import styles from "./PreconfirmationStep.module.scss";
import { RootState } from "../../../app/store";
import { FormikValues } from "formik";
import useTemplateInterpreter from "../../../hooks/useTemplateInterpreter";

const PreconfirmationStep = ({
  name,
  columns,
  fields,
  fieldsWithSeparator,
  editStep,
  isDocumentStep,
}: PreconfirmationStep) => {
  const {
    stepContainer,
    header,
    body,
    field: fieldStyle,
    linksContainer,
    editButton: editButtonStyle,
    accordion: accordionStyle,
  } = styles;

  const state = useAppSelector((state: RootState) => state);

  const { onboardingData, securities, activeTemplate } = state;

  const { onboardingType } = activeTemplate;

  const { formValues } =
    onboardingType === "SECURITIES" ? securities : onboardingData;

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { urlTemplate } = useTemplateInterpreter({
    dispatch,
    onboardingType,
    state,
  });

  const handleEdit = ({
    step,
    substep,
  }: {
    step: number;
    substep?: number;
  }) => {
    dispatch(onUpdatePreconfirmation(true));
    dispatch(
      onboardingType === "SECURITIES"
        ? onUpdateStepSecurities(Number(step))
        : onUpdateStep(Number(step)),
    );
    substep &&
      dispatch(
        onboardingType === "SECURITIES"
          ? onUpdateSubStepSecurities(substep)
          : onUpdateSubStep(substep),
      );
    navigate(urlTemplate);
  };

  const conditionalFieldHasValue = (
    name: string,
    values: FormikValues | null = null,
  ) => {
    if (!formValues) return false;

    const splittedName = name.split(".");
    const nestedValue = splittedName.reduce(
      (acc, key) => acc?.[key],
      values ?? formValues,
    );

    if (Array.isArray(nestedValue) && nestedValue.length === 0) return false;

    return !!nestedValue;
  };

  const getValue = (field: PreconfirmationField) => {
    const {
      name,
      valueGetter,
      values,
      type,
      options,
      formatInPreConfirmation,
      canUploadMultipleFiles,
    } = field;
    if (!formValues) return "-";

    if (valueGetter) return valueGetter(values ?? formValues);

    const splittedName = name.split(".");

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let nestedValue: any = splittedName.reduce(
      (acc, key) => acc?.[key],
      values ?? formValues,
    );

    // Get the label of the selected options
    if (options) {
      const hasMultipleValues = Array.isArray(nestedValue);
      const selectedOptions = hasMultipleValues
        ? nestedValue?.flatMap((val: string) =>
            options?.filter((option) => option.value === val),
          )
        : options?.find((option) => option.value === nestedValue);

      nestedValue = hasMultipleValues
        ? selectedOptions?.map((option: Option) => option.label).join(", ")
        : selectedOptions?.label;
    }

    if (formatInPreConfirmation)
      nestedValue = formatInPreConfirmation(nestedValue, formValues);

    if (type === "file") {
      if (canUploadMultipleFiles) {
        const files = nestedValue;
        if (files && Array.isArray(files)) {
          if (files.length === 0) return "-";
          return files
            ?.map(
              (f: File & { originalFileName?: string }) =>
                f.name || f.originalFileName || "-",
            )
            .join(", ");
        } else {
          return "-";
        }
      } else {
        const file = nestedValue;
        return file ? file.name : "-";
      }
    }

    if (isDocumentStep) {
      const docs = nestedValue as DocumentMetadata[];
      const documentLinks = docs?.map(({ originalFileName, publicUrl }) => ({
        label: originalFileName,
        value: publicUrl,
      }));

      return (
        <div className={linksContainer}>
          {!docs && "-"}
          {documentLinks?.map(({ label, value }) => {
            return (
              <a key={value} href={value} target="_blank">
                {label}
              </a>
            );
          })}
        </div>
      );
    }

    return nestedValue || "-";
  };

  const dimensions = useWindowWidth();
  const isMobile = dimensions.width < 768;

  const StepBody = (fields: PreconfirmationField[]) => {
    return (
      <div
        className={body}
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {fields?.map((field) => {
          const { name, label, isConditional, values } = field;

          if (!isConditional || conditionalFieldHasValue(name, values))
            return (
              <div key={name} className={fieldStyle}>
                <Typography type="LABEL" size="M" weight="EMPHASIS" tag="span">
                  {label}
                </Typography>
                <Typography type="BODY" size="S" tag="div">
                  {getValue(field)}
                </Typography>
              </div>
            );
        })}
      </div>
    );
  };

  const editButton = editStep && (
    <Button
      text="Editar"
      variant="tertiary"
      className={editButtonStyle}
      onClick={() => handleEdit(editStep)}
      trailingIcon={<Icon name="edit" />}
    />
  );

  const stepBody = (
    <>
      {!fieldsWithSeparator && StepBody(fields || [])}
      {fieldsWithSeparator?.map((array, i) => {
        return (
          <Fragment key={i}>
            {StepBody(array)}
            {i !== fieldsWithSeparator.length - 1 && <hr />}
          </Fragment>
        );
      })}
    </>
  );

  if (isMobile)
    return (
        <Accordion
          title={name}
          subtitle={editButton}
          className={accordionStyle}
        >
          {stepBody}
        </Accordion>
    );

  return (
    <section className={stepContainer}>
      <div className={header}>
        <Typography type="HEADING" size="M" tag="h3">
          {name}
        </Typography>
        {editButton}
      </div>
      {stepBody}
    </section>
  );
};

export default PreconfirmationStep;
